import {
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useId,
  useRef,
} from 'react'
import { threadCopy } from '@/copy/thread'
import { Alert } from '../Alert/Alert'
import { Button } from '../Button/Button'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import styles from './Composer.module.css'
import { useDrawnCaret } from './useDrawnCaret'

type FieldProps = Omit<
  ComponentPropsWithoutRef<'textarea'>,
  | 'value'
  | 'onChange'
  | 'placeholder'
  | 'disabled'
  | 'className'
  | 'style'
  | 'id'
  | 'rows'
  | 'title'
>

const finePointer = () =>
  typeof window.matchMedia !== 'function' ||
  window.matchMedia('(pointer: fine)').matches

/**
 * The row pinned to the end of the thread column. The row is the field: the
 * text has no box of its own, and while it has focus the row's top rule
 * turns accent (a field's indicator, not the ring).
 * The caret is drawn, not the browser's. Enter sends on a fine pointer and
 * Shift+Enter breaks a line; a touch keyboard's return breaks a line and Send
 * sends. The value is never cleared here: the owner clears it once the send
 * is acknowledged.
 */
export function Composer({
  value,
  onValue,
  onSend,
  label,
  openThreadId,
  mentions,
  fieldProps,
  ref,
  frozen,
  error,
  onRetry,
  onStop,
  className,
}: {
  value: string
  onValue: (value: string) => void
  onSend: () => void
  /** The destination, such as "New message in Incident runbooks". It names the
   * field and, with an ellipsis, is the placeholder. */
  label: string
  /** Set when a thread is open: the composer replies into it. */
  openThreadId?: string | null
  /** The mention list, rendered above the row. */
  mentions?: ReactNode
  /** Props for the textarea; a mention list passes its combobox wiring here. */
  fieldProps?: FieldProps
  ref?: Ref<HTMLTextAreaElement>
  /** Why the composer is disabled; shown beside the row. */
  frozen?: string
  /** A failed send; the draft is kept and `onRetry` offers the retry. */
  error?: string
  onRetry?: () => void
  onStop?: () => void
  className?: string
}) {
  const fieldId = useId()
  const reasonId = useId()
  const fieldRef = useRef<HTMLTextAreaElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const markerRef = useRef<HTMLSpanElement | null>(null)

  const setFieldRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      fieldRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) ref.current = el
    },
    [ref],
  )

  const { caret, selection, handlers } = useDrawnCaret({
    fieldRef,
    wrapRef,
    markerRef,
    value,
    disabled: !!frozen,
  })

  const canSend = !frozen && value.trim() !== ''

  function send() {
    if (canSend) onSend()
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    fieldProps?.onKeyDown?.(e)
    // The mention list owns the key when it handled it.
    if (e.defaultPrevented) return
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.nativeEvent.isComposing &&
      e.keyCode !== 229 &&
      finePointer()
    ) {
      e.preventDefault()
      send()
    }
  }

  const describedBy =
    [fieldProps?.['aria-describedby'], frozen ? reasonId : undefined]
      .filter(Boolean)
      .join(' ') || undefined

  const before = value.slice(0, selection)
  const after = value.slice(selection)

  return (
    <div
      className={cx(styles.composer, className)}
      data-mode={openThreadId ? 'reply' : 'new'}
    >
      {mentions}
      {error && (
        <div className={styles.notice}>
          <Alert tone="danger">
            <span className={styles.alertRow}>
              {error}
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  {threadCopy.composer.retry}
                </Button>
              )}
            </span>
          </Alert>
        </div>
      )}
      {frozen && (
        <div className={styles.notice}>
          <span id={reasonId} className={styles.reason}>
            {frozen}
          </span>
        </div>
      )}
      <div className={styles.row}>
        <span className={styles.prompt} aria-hidden="true">
          <Icon
            name={openThreadId ? 'corner-bottom-left' : 'chevron-right'}
            size="icon-embedded"
          />
        </span>
        <div className={styles.fieldWrap} ref={wrapRef}>
          <label htmlFor={fieldId} className={styles.visuallyHidden}>
            {label}
          </label>
          <textarea
            {...fieldProps}
            ref={setFieldRef}
            id={fieldId}
            rows={1}
            className={styles.field}
            value={value}
            onChange={(e) => onValue(e.target.value)}
            placeholder={threadCopy.composer.placeholder(label)}
            disabled={!!frozen}
            aria-describedby={describedBy}
            onKeyDown={onKeyDown}
            onFocus={(e) => {
              fieldProps?.onFocus?.(e)
              handlers.onFocus()
            }}
            onBlur={(e) => {
              fieldProps?.onBlur?.(e)
              handlers.onBlur()
            }}
            onSelect={(e) => {
              fieldProps?.onSelect?.(e)
              handlers.onSelect()
            }}
            onScroll={(e) => {
              fieldProps?.onScroll?.(e)
              handlers.onScroll()
            }}
          />
          <div className={styles.mirror} aria-hidden="true">
            {before}
            <span ref={markerRef} data-marker="" />
            {after}{' '}
          </div>
          {caret && (
            <span
              className={styles.caret}
              aria-hidden="true"
              data-caret=""
              style={{
                '--local-caret-x': `${caret.x}px`,
                '--local-caret-y': `${caret.y}px`,
              }}
            />
          )}
        </div>
        <button
          type="button"
          className={styles.send}
          aria-label={threadCopy.composer.send}
          aria-disabled={!canSend || undefined}
          data-ready={canSend ? '' : undefined}
          onClick={send}
        >
          <Icon name="arrow-up" size="icon-embedded" />
        </button>
        {onStop && (
          <span className={styles.stop}>
            <Button variant="outline" onClick={onStop}>
              {threadCopy.composer.stop}
            </Button>
          </span>
        )}
      </div>
    </div>
  )
}
